import React, { useState, useEffect, useRef } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Form, 
  Table, 
  Button, 
  InputGroup, 
  FormControl, 
  Spinner,
  Card,
  Dropdown,
  Modal,
  Pagination
} from 'react-bootstrap';
import { FaSearch, FaPlus, FaEllipsisV, FaEye, FaEdit, FaTrash, FaTimes } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import TransportService from '../services/TransportService';
import UserService from '../services/UserService';
import ConfirmModal from './ConfirmModal';
import TransportForm from '../components/Transport/TransportForm';
import {formatCurrency} from "../utils/formatters";

const Transport = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [owners, setOwners] = useState([]);
  const [transportTypes, setTransportTypes] = useState([]);
  const [filters, setFilters] = useState({
    ega_id: '',
    tur_nomi: 'Калла',
    search: '',
    days: 0
  });
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editFormData, setEditFormData] = useState(null);
  const formRef = useRef(null);
  const navigate = useNavigate();
  const [expiryFilter, setExpiryFilter] = useState('0');
  const [balanceFilter, setBalanceFilter] = useState('0');

  // Available per page options
  const perPageOptions = [5, 10, 20, 50, 100];

  // Load data when component mounts or filters change
  useEffect(() => {
    loadData();
    // We don't include currentPage in the dependency array to prevent infinite loops
    // Instead, we'll handle page changes through the handlePageChange function
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perPage, filters, currentPage, expiryFilter, balanceFilter]);
  
  // Load owners and transport types on component mount
  useEffect(() => {
    loadOwners();
    loadTransportTypes();
  }, []);

  // Handle edit button click
  const handleEditClick = (item) => {
    setSelectedItem(item);
    console.log(item)
    // Transform item data to match form initial values
    setEditFormData({
      name: item.name || '',
      davlat_raqam: item.davlat_raqam || '',
      tex_passport_raqam: item.tex_passport_raqam || '',
      rang: item.rang || '',
      tur_id: item.tur?.id || '',
      ega_id: item.ega?.id || '',
      izoh: item.izoh || ''
    });
    setShowEditModal(true);
  };
  
  // Handle form submission from edit modal
  const handleEditSubmit = async (values) => {
    try {
      // Update transport
      const res = await TransportService.updateTransport(selectedItem.id, values);
      if (res && res.success === false) {
        // toast.error("Transport tahrirlashda xatolik, ehtimol tex passport yoki davlat raqami mavjud bolishi mumkin");
        return
      }
      if (res?.success === false) {
        return;
      }
      toast.success('Transport muvaffaqiyatli yangilandi');
      setShowEditModal(false);
      loadData(); // Refresh the list
    } catch (error) {
      console.error('Error updating transport:', error);
      toast.error(error.response?.data?.message || 'Xatolik yuz berdi');
    } finally {
      // setSubmitting(false);
    }
  };
  
  // Handle page change
  const handlePageChange = (page) => {
    console.log('Changing to page:', page);
    setCurrentPage(page);
    // Load data for the new page
    // loadData();
    // Scroll to top of the table
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const loadData = async () => {
    try {
      setLoading(true);
      console.log('Loading data with params:', {
        ega_id: filters.ega_id || undefined,
        tur_nomi: filters.tur_nomi || undefined,
        search: filters.search || undefined,
        days: expiryFilter || 0,
        per_page: perPage,
        page_number: currentPage
      });
      
      const response = await TransportService.getTransportListByFilters(
        filters.ega_id || undefined,
        filters.tur_nomi || undefined,
        filters.search || undefined,
        expiryFilter || 0,
        balanceFilter || 0,
        perPage,
        currentPage
      );
      
      console.log('API Response:', response);
      
      // Update state with paginated data
      setList(response.data.data || []);
      setTotalItems(response.data?.total || 0);
      setTotalPages(Math.ceil(response.data?.total / perPage) || 1);
      
      // If we got an empty response and we're not on the first page, go to first page
      if (response.data?.length === 0 && currentPage > 1) {
        console.log('No data on page', currentPage, ', going to page 1');
        setCurrentPage(1);
      }
    } catch (error) {
      console.error('Error loading transport list:', error);
      toast.error('Transportlar ro\'yxatini yuklashda xatolik yuz berdi');
    } finally {
      setLoading(false);
    }
  };

  const loadOwners = async () => {
    try {
      const response = await UserService.getUserList();
      setOwners(response.data || []);
    } catch (error) {
      console.error('Error loading owners:', error);
    }
  };

  const loadTransportTypes = async () => {
    try {
      const response = await TransportService.getTransportTypes();
      setTransportTypes(response.data || []);
      
      // Set 'Калла' as the default filter if it exists in the transport types
      const kallaType = response.data?.find(type => type.name === 'Калла');
      if (kallaType) {
        setFilters(prev => ({
          ...prev,
          tur_nomi: 'Калла'
        }));
      }
    } catch (error) {
      console.error('Error loading transport types:', error);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setCurrentPage(1); // Reset to first page when filters change
  };
  
  const handlePerPageChange = (e) => {
    const newPerPage = parseInt(e.target.value, 10);
    setPerPage(newPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    // The search is already handled by the controlled input
    setCurrentPage(1);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    try {
      await TransportService.deleteTransport(itemToDelete.id);
      toast.success('Transport muvaffaqiyatli o\'chirildi');
      loadData();
    } catch (error) {
      console.error('Error deleting transport:', error);
      toast.error('Transportni o\'chirishda xatolik yuz berdi');
    } finally {
      setShowConfirmModal(false);
      setItemToDelete(null);
    }
  };

  const handleShowDetails = (item) => {
    setSelectedItem(item);
    setShowDetailsModal(true);
  };

  const getDayDiff = (gacha) => {
    if (!gacha || gacha === '' || gacha === 'undefined') return '-';
    const now = new Date();
    return Math.ceil((new Date(gacha) - now) / (1000 * 60 * 60 * 24));
  };
  const getColorClass = (days) => {
    const day = days || 0;
    if (day === '-') return '';
    if (day < 0) return 'text-danger fw-bold';
    if (day < 7) return 'text-warning fw-bold';
    return 'text-success fw-bold';
  };
  const getColorClassForBalance = (amount) => {
    const am = amount || 0;
    if (am < 0) return 'text-danger fw-bold';
    return 'text-success fw-bold';
  };

  return (
    <Container fluid className="py-4">
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Transportlar ro'yxati</h5>
          <Button 
            variant="primary" 
            onClick={() => navigate('/dashboard/transport/add')}
          >
            <FaPlus className="me-2" /> Qo'shish
          </Button>
        </Card.Header>
        <Card.Body>
          {/* Filters */}
          <Form onSubmit={handleSearch} className="mb-4">
            <Row>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Muddati</Form.Label>
                  <Form.Select value={expiryFilter} onChange={e => setExpiryFilter(e.target.value)}>
                    <option value="0">Hammasi</option>
                    <option value="-1">Muddati o'tganlar</option>
                    <option value="7">Kam qolganlar (&lt;7 kun)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Balans</Form.Label>
                  <Form.Select value={balanceFilter} onChange={e => setBalanceFilter(e.target.value)}>
                    <option value="0">Hammasi</option>
                    <option value="-1">Qarzdorlar</option>
                    <option value="1">Haqdorlar</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Transport turi</Form.Label>
                  <Form.Select
                      name="tur_nomi"
                      value={filters.tur_nomi}
                      onChange={handleFilterChange}
                  >
                    <option value="">Barcha turlari</option>
                    {transportTypes.map(type => (
                        <option key={type.id} value={type.name}>
                          {type.name}
                        </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Egasi</Form.Label>
                  <Form.Select 
                    name="ega_id"
                    value={filters.ega_id}
                    onChange={handleFilterChange}
                  >
                    <option value="">Barchasi</option>
                    {owners.map(owner => (
                      <option key={owner.id} value={owner.id}>
                        {owner.name + ' -> ' + owner.phone || 'Noma\'lum'}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>


              {/*<Col md={3}>*/}
              {/*  <Form.Group className="mb-3">*/}
              {/*    <Form.Label>Muddati</Form.Label>*/}
              {/*    <Form.Select */}
              {/*      value={filters.days}*/}
              {/*      onChange={(e) => handleDaysFilterChange(e.target.value)}*/}
              {/*    >*/}
              {/*      <option value="0">Barchasi</option>*/}
              {/*      <option value="7">1 hafta ichida tugaydiganlar</option>*/}
              {/*      <option value="30">1 oy ichida tugaydiganlar</option>*/}
              {/*      <option value="90">3 oy ichida tugaydiganlar</option>*/}
              {/*    </Form.Select>*/}
              {/*  </Form.Group>*/}
              {/*</Col>*/}
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Qidirish</Form.Label>
                  <InputGroup>
                    <FormControl
                      name="search"
                      placeholder="Nomi, davlat raqami yoki tex pasport raqami..."
                      value={filters.search}
                      onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    />
                    <Button variant="outline-secondary" type="submit">
                      <FaSearch />
                    </Button>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
          </Form>

          {/* Data Table */}
          <div className="table-responsive">
            <Table striped bordered hover>
              <thead className='table-dark'>
                <tr>
                  <th>#</th>
                  <th>Nomi</th>
                  <th>Davlat raqami</th>
                  <th>Tex pasport raqami</th>
                  <th>Balansi</th>
                  <th>Putyovka muddat</th>
                  <th>Ijara muddat</th>
                  <th>Litsenziya muddat</th>
                  <th>Rangi</th>
                  <th>Egasi</th>
                  <th>Turi</th>
                  <th>Amallar</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr className='w-100'>
                    <td colSpan="8" className="text-center w-100">
                      <Spinner animation="border" variant="primary" />
                    </td>
                  </tr>
                ) : list.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center w-100">Ma'lumot topilmadi</td>
                  </tr>
                ) : (
                  list.map((item, index) => {
                    const dayput = getDayDiff(item.putyofkas_closest_expiry);
                    const dayIj = getDayDiff(item.ijara_closest_expiry);
                    const dayLit = getDayDiff(item.litsenziya_closest_expiry);
                    return (
                    <tr key={item.id}>
                      <td>{(currentPage - 1) * perPage + index + 1}</td>
                      <td>{item.name}</td>
                      <td>{item.davlat_raqam}</td>
                      <td>{item.tex_passport_raqam}</td>
                      <td className={getColorClassForBalance(item.balance)}>{formatCurrency(item.balance)}</td>
                      <td className={getColorClass(dayput)}>{dayput}</td>
                      <td className={getColorClass(dayIj)}>{dayIj}</td>
                      <td className={getColorClass(dayLit)}>{dayLit}</td>
                      <td>{item.rang}</td>
                      <td>{item.ega?.name || 'Noma\'lum'}</td>
                      <td>{item.tur?.name || 'Noma\'lum'}</td>
                      <td>
                        <div className="d-flex">
                          <Button 
                            variant="link" 
                            size="md"
                            className="text-success p-0 me-2"
                            onClick={() => handleEditClick(item)}
                            title="Tahrirlash"
                          >
                            <FaEdit />
                          </Button>
                          <Button 
                            variant="link" 
                            size="md"
                            className="text-danger p-0 me-2"
                            onClick={() => {
                              setItemToDelete(item);
                              setShowConfirmModal(true);
                            }}
                            title="O'chirish"
                          >
                            <FaTrash />
                          </Button>
                          <Button
                              variant="link"
                              size="md"
                              className="text-primary p-0"
                              onClick={() => navigate(`/dashboard/transport/show/${item.id}`)}
                              title="Batafsil ko'rish"
                          >
                            <FaEye />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )})
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination and Per Page Selector */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="d-flex align-items-center">
              <span className="me-2">Har sahifada:</span>
              <Form.Select 
                style={{ width: '80px' }} 
                value={perPage}
                onChange={handlePerPageChange}
                disabled={loading}
              >
                {perPageOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </Form.Select>
              <span className="ms-2">
                {loading ? 'Yuklanmoqda...' : `Jami: ${totalItems} ta`}
              </span>
            </div>
            {/*{totalPages > 1 && (*/}
              <nav>
                <ul className="pagination mb-0">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1 || loading}
                    >
                      &laquo;
                    </button>
                  </li>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => handlePageChange(page)}
                        disabled={loading}
                      >
                        {page}
                      </button>
                    </li>
                  ))}
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages || loading}
                    >
                      &raquo;
                    </button>
                  </li>
                </ul>
              </nav>
            {/*)}*/}
          </div>
        </Card.Body>
      </Card>

      {/* Details Modal */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Transport ma'lumotlari</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedItem && (
            <Row>
              <Col md={6}>
                <p><strong>Nomi:</strong> {selectedItem.name}</p>
                <p><strong>Davlat raqami:</strong> {selectedItem.davlat_raqam}</p>
                <p><strong>Tex pasport raqami:</strong> {selectedItem.tex_passport_raqam}</p>
                <p><strong>Rangi:</strong> {selectedItem.rang}</p>
              </Col>
              <Col md={6}>
                <p><strong>Egasi:</strong> {selectedItem.ega?.name || 'Noma\'lum'}</p>
                <p><strong>Telefon raqami:</strong> {selectedItem.ega?.phone || 'Mavjud emas'}</p>
                <p><strong>Transport turi:</strong> {selectedItem.tur?.name || 'Noma\'lum'}</p>
                <p><strong>Yaratilgan sana:</strong> {new Date(selectedItem.created_at).toLocaleDateString()}</p>
              </Col>
              <Col xs={12}>
                <p><strong>Izoh:</strong> {selectedItem.izoh || 'Mavjud emas'}</p>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Yopish
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        show={showConfirmModal}
        onHide={() => {
          setShowConfirmModal(false);
          setItemToDelete(null);
        }}
        onConfirm={handleDelete}
        title="O'chirishni tasdiqlang"
        message={`${itemToDelete?.name} transportini o'chirishni istaysizmi?`}
      />

      {/* Edit Modal */}
      <Modal 
        show={showEditModal} 
        onHide={() => setShowEditModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Transportni tahrirlash</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editFormData && (
            <TransportForm
              initialValues={editFormData}
              onSubmit={handleEditSubmit}
              formRef={formRef}
              isEditing={true}
              transportTypes={transportTypes}
              owners={owners}
            />
          )}
        </Modal.Body>
        {/*<Modal.Footer>*/}
        {/*  <Button */}
        {/*    variant="secondary" */}
        {/*    onClick={() => setShowEditModal(false)}*/}
        {/*  >*/}
        {/*    Bekor qilish*/}
        {/*  </Button>*/}
        {/*  <Button */}
        {/*    variant="primary" */}
        {/*    onClick={() => formRef.current?.submitForm()}*/}
        {/*  >*/}
        {/*    Saqlash*/}
        {/*  </Button>*/}
        {/*</Modal.Footer>*/}
      </Modal>
    </Container>
  );
};

export default Transport;
